import React, { useState, useEffect } from 'react';
import './GapAnalyseModal.css';

const GapAnalyseModal = ({ gap, prozessName, anwendungName, onSave, onClose }) => {
    const [formData, setFormData] = useState({
        massnahme_beschreibung: '',
        massnahme_typ: '',
        massnahme_status: 'offen',
        verantwortlicher: '',
        frist: '',
    });

    useEffect(() => {
        if (gap) {
            setFormData({
                massnahme_beschreibung: gap.massnahme_beschreibung || '',
                massnahme_typ: gap.massnahme_typ || '',
                massnahme_status: gap.massnahme_status || 'offen',
                verantwortlicher: gap.verantwortlicher || '',
                frist: gap.frist || '',
            });
        }
    }, [gap]);

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('de-DE');
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content gap-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <div className="modal-header-content">
                        <h2>📝 Maßnahmen bearbeiten</h2>
                        <div className="modal-context-badges">
                            {gap.prozess_id && (
                                <div className="context-badge prozess-badge">
                                    Prozess
                                </div>
                            )}
                            {gap.anwendung_id && (
                                <div className="context-badge anwendung-badge">
                                    Anwendung
                                </div>
                            )}
                        </div>
                    </div>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>

                <div className="modal-body">
                    {/* READ-ONLY GAP-Informationen */}
                    <div className="gap-info-section">
                        <h3>ℹ️ GAP-Details (nicht editierbar)</h3>
                        <div className="info-grid">
                            {/* Prozess/Anwendung Namen ganz oben */}
                            {prozessName && (
                                <div className="info-item full-width">
                                    <label>Prozess:</label>
                                    <div className="info-value prozess-value">{prozessName}</div>
                                </div>
                            )}
                            {anwendungName && (
                                <div className="info-item full-width">
                                    <label>Anwendung:</label>
                                    <div className="info-value anwendung-value">{anwendungName}</div>
                                </div>
                            )}

                            <div className="info-item">
                                <label>GAP-Titel:</label>
                                <div className="info-value">{gap.gap_titel}</div>
                            </div>
                            <div className="info-item">
                                <label>Kritikalität:</label>
                                <div className="info-value">
                                    <span className={`kritikalitaet-badge kritikalitaet-${gap.kritikalitaet}`}>
                                        {gap.kritikalitaet?.toUpperCase()}
                                    </span>
                                </div>
                            </div>
                            <div className="info-item full-width">
                                <label>Beschreibung:</label>
                                <div className="info-value">{gap.gap_beschreibung}</div>
                            </div>
                            <div className="info-item">
                                <label>Kategorie:</label>
                                <div className="info-value">{gap.gap_kategorie || '-'}</div>
                            </div>
                            <div className="info-item">
                                <label>GAP-Typ:</label>
                                <div className="info-value">{gap.gap_typ?.replace(/_/g, ' ') || '-'}</div>
                            </div>
                            <div className="info-item full-width">
                                <label>Erkennungsgrund:</label>
                                <div className="info-value erkennungs-grund">
                                    {gap.erkennungs_grund
                                        ? gap.erkennungs_grund.replace(/Automatisch erkannt:\s*/gi, '').trim()
                                        : '-'
                                    }
                                </div>
                            </div>
                            <div className="info-item">
                                <label>Kontrollanforderung:</label>
                                <div className="info-value">{gap.kontrollanforderung || '-'}</div>
                            </div>
                            <div className="info-item">
                                <label>Ist-Zustand:</label>
                                <div className="info-value">{gap.ist_zustand || '-'}</div>
                            </div>
                            <div className="info-item">
                                <label>Soll-Zustand:</label>
                                <div className="info-value">{gap.soll_zustand || '-'}</div>
                            </div>
                            <div className="info-item">
                                <label>Regulatorisch relevant:</label>
                                <div className="info-value">{gap.regulatorische_relevanz ? '✅ Ja' : '❌ Nein'}</div>
                            </div>
                            <div className="info-item">
                                <label>Muss behoben werden:</label>
                                <div className="info-value">{gap.muss_behoben ? '✅ Ja' : '❌ Nein'}</div>
                            </div>
                            <div className="info-item">
                                <label>Erstellt am:</label>
                                <div className="info-value">{formatDate(gap.created_at)}</div>
                            </div>
                            <div className="info-item">
                                <label>Letzte Prüfung:</label>
                                <div className="info-value">{formatDate(gap.letzte_pruefung)}</div>
                            </div>
                        </div>
                    </div>

                    {/* EDITABLE Maßnahmen-Felder */}
                    <form onSubmit={handleSubmit}>
                        <div className="massnahmen-section">
                            <h3>🔧 Maßnahmen (editierbar)</h3>

                            <div className="form-group">
                                <label htmlFor="massnahme_beschreibung">
                                    Maßnahmenbeschreibung *
                                </label>
                                <textarea
                                    id="massnahme_beschreibung"
                                    name="massnahme_beschreibung"
                                    value={formData.massnahme_beschreibung}
                                    onChange={handleChange}
                                    rows={4}
                                    placeholder="Beschreiben Sie die geplante Maßnahme zur Behebung dieser GAP..."
                                    required
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="massnahme_typ">
                                        Maßnahmentyp *
                                    </label>
                                    <select
                                        id="massnahme_typ"
                                        name="massnahme_typ"
                                        value={formData.massnahme_typ}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="">Bitte wählen...</option>
                                        <option value="technisch">Technisch</option>
                                        <option value="organisatorisch">Organisatorisch</option>
                                        <option value="prozessual">Prozessual</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="massnahme_status">
                                        Maßnahmenstatus *
                                    </label>
                                    <select
                                        id="massnahme_status"
                                        name="massnahme_status"
                                        value={formData.massnahme_status}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="offen">Offen</option>
                                        <option value="in_planung">In Planung</option>
                                        <option value="in_umsetzung">In Umsetzung</option>
                                        <option value="abgeschlossen">Abgeschlossen</option>
                                        <option value="zurueckgestellt">Zurückgestellt</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="verantwortlicher">
                                        Verantwortlicher
                                    </label>
                                    <input
                                        type="text"
                                        id="verantwortlicher"
                                        name="verantwortlicher"
                                        value={formData.verantwortlicher}
                                        onChange={handleChange}
                                        placeholder="Name des Verantwortlichen"
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="frist">
                                        Frist
                                    </label>
                                    <input
                                        type="date"
                                        id="frist"
                                        name="frist"
                                        value={formData.frist}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button type="button" className="btn-secondary" onClick={onClose}>
                                Abbrechen
                            </button>
                            <button type="submit" className="btn-primary">
                                💾 Maßnahmen speichern
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default GapAnalyseModal;